/* eslint-disable jsx-a11y/label-has-associated-control */
import { useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { Link } from 'react-router-dom';
import type { UseMutationResult } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';

import { Stepper } from './Stepper';

import { Button } from '@/components/ui/button';
import {
    Card,
    CardContent,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    useNepalCities,
    useNepalStates,
} from '@/hooks/use-nepal-locations';
import { cn } from '@/lib/utils';

interface Step2Data {
    businessName: string;
    ownerName: string;
    contactNo: string;
    panVat: string;
    address: string;
    state: string;
    city: string;
    ward: string;
    acceptPolicies: true;
    acceptUpdates?: boolean;
}

type TextFieldName =
    | 'businessName' | 'ownerName' | 'contactNo' | 'panVat'
    | 'address' | 'state' | 'city' | 'ward';

interface TextFieldProps {
    form: UseFormReturn<Step2Data>;
    name: TextFieldName;
    label: string;
    placeholder: string;
    type?: string;
}

function TextField({
    form,
    name,
    label,
    placeholder,
    type = 'text',
}: TextFieldProps) {
    const error = form.formState.errors[name];
    return (
        <div className="grid gap-1.5">
            <Label htmlFor={name}>{label}</Label>
            <Input
                id={name}
                type={type}
                placeholder={placeholder}
                {...form.register(name)}
                onWheel={type === 'number' ? (e) => e.currentTarget.blur() : undefined}
                className={cn(
                    { 'focus-visible:ring-destructive': error },
                    type === 'number' && '[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none',
                )}
            />
            {error && <span className="text-sm text-destructive">{error.message}</span>}
        </div>
    );
}

function LocationFields({ form }: { form: UseFormReturn<Step2Data> }) {
    const { states, loading: statesLoading } = useNepalStates();
    const [stateId, setStateId] = useState<number | null>(null);
    const { cities, loading: citiesLoading } = useNepalCities(stateId);
    const stateError = form.formState.errors.state;
    const cityError = form.formState.errors.city;
    let cityPlaceholder = 'Select city';
    if (!stateId) cityPlaceholder = 'Select state first';
    else if (citiesLoading) cityPlaceholder = 'Loading...';

    return (
        <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
                <Label htmlFor="state">State *</Label>
                <Select
                    value={form.watch('state') || undefined}
                    onValueChange={(name) => {
                        const match = states.find((state) => state.name === name);
                        setStateId(match?.id ?? null);
                        form.setValue('state', name, { shouldValidate: true });
                        form.setValue('city', '');
                    }}
                    disabled={statesLoading}
                >
                    <SelectTrigger id="state" className="w-full" aria-invalid={!!stateError}>
                        <SelectValue placeholder={statesLoading ? 'Loading...' : 'Select state'} />
                    </SelectTrigger>
                    <SelectContent>
                        {states.map((state) => (
                            <SelectItem key={state.id} value={state.name}>{state.name}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                {stateError && <span className="text-sm text-destructive">{stateError.message}</span>}
            </div>
            <div className="grid gap-1.5">
                <Label htmlFor="city">City *</Label>
                <Select
                    value={form.watch('city') || undefined}
                    onValueChange={(name) => form.setValue('city', name, { shouldValidate: true })}
                    disabled={!stateId || citiesLoading}
                >
                    <SelectTrigger id="city" className="w-full" aria-invalid={!!cityError}>
                        <SelectValue placeholder={cityPlaceholder} />
                    </SelectTrigger>
                    <SelectContent>
                        {cities.map((city) => (
                            <SelectItem key={city.id} value={city.name}>{city.name}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                {cityError && <span className="text-sm text-destructive">{cityError.message}</span>}
            </div>
        </div>
    );
}

interface SignupStep2Props {
    form: UseFormReturn<Step2Data>;
    onSubmit: (data: Step2Data) => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    registerMutation: UseMutationResult<any, Error, any, unknown>;
    handleBack: () => void;
}

export function SignupStep2({
    form,
    onSubmit,
    registerMutation,
    handleBack,
}: SignupStep2Props) {
    return (
        <div className="min-h-screen bg-background flex flex-col">
            <div className="p-6">
                <button
                    type="button"
                    onClick={handleBack}
                    className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                    <ArrowLeft className="h-4 w-4" />
                    Go Back
                </button>
            </div>

            <div className="flex-1 flex items-center justify-center px-4 pb-12">
                <div className="flex flex-col gap-6 w-full max-w-md">
                    <div className="flex flex-col items-center gap-3">
                        <Link to="/"><img src="/chanco-vertical.png" alt="Chanco" className="h-24 w-auto" /></Link>
                        <h1 className="text-2xl font-bold text-foreground">Additional Details</h1>
                        <p className="text-sm text-muted-foreground">Please enter your details to register</p>
                    </div>

                    <Stepper activeStep={2} />

                    <Card className="border-0 shadow-lg">
                        <CardContent className="pt-6">
                            <form onSubmit={form.handleSubmit(onSubmit)}>
                                <div className="grid gap-4">
                                    <TextField
                                        form={form}
                                        name="businessName"
                                        label="Name of Business *"
                                        placeholder="Enter business name"
                                    />
                                    <TextField
                                        form={form}
                                        name="ownerName"
                                        label="Owner Name *"
                                        placeholder="Enter owner name"
                                    />
                                    <TextField
                                        form={form}
                                        name="contactNo"
                                        label="Contact No *"
                                        placeholder="Enter contact number"
                                    />

                                    <div className="h-px bg-border my-1" />

                                    <TextField
                                        form={form}
                                        name="panVat"
                                        label="PAN/VAT No *"
                                        placeholder="Enter PAN/VAT number"
                                    />
                                    <TextField
                                        form={form}
                                        name="address"
                                        label="Address *"
                                        placeholder="Enter address"
                                    />
                                    <LocationFields form={form} />
                                    <TextField
                                        form={form}
                                        name="ward"
                                        label="Ward No *"
                                        placeholder="Ward number"
                                        type="number"
                                    />

                                    <div className="mt-1 grid gap-3 rounded-xl border border-border bg-muted/40 p-4">
                                        <div className="flex items-start gap-3">
                                            <input id="acceptPolicies" type="checkbox" {...form.register('acceptPolicies')} className="mt-0.5 h-4 w-4 shrink-0 rounded border-input accent-primary" aria-invalid={!!form.formState.errors.acceptPolicies} />
                                            <label
                                                htmlFor="acceptPolicies"
                                                className="cursor-pointer text-sm leading-relaxed text-foreground"
                                            >
                                                I&apos;ve read and agree to Chanco&apos;s
                                                {' '}
                                                <Link to="/chanco/privacy-policy" target="_blank" rel="noopener noreferrer" className="font-medium text-primary underline-offset-4 hover:underline">
                                                    Privacy Policy
                                                </Link>
                                                {' '}
                                                and
                                                {' '}
                                                <Link to="/chanco/terms-and-conditions" target="_blank" rel="noopener noreferrer" className="font-medium text-primary underline-offset-4 hover:underline">
                                                    Terms &amp; Conditions
                                                </Link>
                                                <span className="text-destructive"> *</span>
                                            </label>
                                        </div>
                                        {form.formState.errors.acceptPolicies && (
                                            <span className="pl-7 text-sm text-destructive">{form.formState.errors.acceptPolicies.message}</span>
                                        )}

                                        <div className="h-px bg-border" />

                                        <div className="flex items-start gap-3">
                                            <input
                                                id="acceptUpdates"
                                                type="checkbox"
                                                {...form.register('acceptUpdates')}
                                                className="mt-0.5 h-4 w-4 shrink-0 rounded border-input accent-primary"
                                            />
                                            <label
                                                htmlFor="acceptUpdates"
                                                className="cursor-pointer text-sm leading-relaxed text-muted-foreground"
                                            >
                                                Keep me posted with product news and updates
                                                from Chanco
                                            </label>
                                        </div>
                                    </div>

                                    <Button type="submit" className="w-full" disabled={registerMutation.isPending}>
                                        {registerMutation.isPending ? 'Registering...' : 'Finish'}
                                    </Button>
                                </div>
                            </form>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
}
